<template>
  <div
    ref="editor"
    class="textEditor ql-editor ql-container ql-snow"
    :id="id"
    @keydown="keydown"
    @focus="onfocus"
    @mouseenter="onfocus"
    :style="{ borderColor: borderColor ? borderColor : '', borderWidth: borderTopWidth ? `${borderTopWidth}px 1px 1px 1px` : 1 }"
  ></div>
</template>

<script>
import Quill from 'quill'

export default {
  props: {
    modelValue: {
      type: String,
      required: false
    },
    id: String,
    index: Number,
    text: String,
    disabled: Boolean,
    borderColor: String,
    borderTopWidth: Number
  },
  data () {
    return {
      // we don't intialise the edotor on mounted
      // instead we wait for the first focus event
      // or when the text is updated by a subcription
      // we make sure to only initialise the editor once
      initialised: false,
      editorText: ''
    }
  },
  methods: {
    initQuillEditor () {
      let element
      this.quill = new Quill(this.$refs.editor, {
        formats: ['bold', 'underline'],
        // readOnly: this.$store.state.isWorkflowDisabled,
        modules: {
          toolbar: false,
          keyboard: {
            bindings: {
              shift_enter: {
                key: 'Enter',
                shiftKey: true,
                handler: (range, ctx) => {
                  this.quill.insertText(range.index, '\n')
                  this.emitTextDelta()
                  return false
                }
              },
              alt_enter: {
                key: 'Enter',
                altKey: true,
                handler: (range, ctx) => {
                  this.quill.insertText(range.index, '\n')
                  this.emitTextDelta()
                  return false
                }
              },
              tab: {
                key: 9,
                handler: (range, ctx) => {
                  const target = this.$refs.editor
                  this.$emit('move-focus', 'right', target)
                }
              }
            }
          }
        },
        theme: 'snow'
      })

      this.quill.keyboard.bindings.Enter.unshift({
        key: 'Enter',
        handler: (range, ctx) => {
          // empty handler necessary to ignore enter
          // and pass it on to the outer handler
          return false
        }
      })

      this.quill.on('text-change', () => {
        this.emitTextDelta()
      })

      this.$nextTick(() => {
        if (this.$refs.editor) {
          element = this.$refs.editor?.firstChild
          element.addEventListener('blur', this.onBlur)
        }
      })

      this.initialised = true
    },

    textCursorIsActive () {
      return this.quill.getSelection() !== null
    },

    onBlur (event) {
      if (event?.relatedTarget?.className === 'ql-clipboard') {
        // avoids saving when pasting into quill editor (content is not always populated)
        return
      }

      if (!this.$refs.editor) return

      const html = this.$refs.editor.childNodes[0].innerHTML
      if (html !== this.editorText) {
        this.$emit('text-update', html, this.id, this.index)
      }
      this.quill.setSelection(null)
    },

    keydown (event) {
      if (this.textCursorIsActive()) {
        if (event.keyCode === 13) { // enter
          if (event.altKey === false && event.shiftKey === false) {
            const target = this.$refs.editor
            this.$emit('move-focus', 'down', target, 'ql-editor')
            this.$nextTick(() => {
              // next tick to allow update of the text (v-model)
              this.$emit('on-enter')
            })
            event.preventDefault()
          }
        }
        if (event.keyCode === 27) { // escape
          const value = this.text || ''
          const delta = this.quill.clipboard.convert({ html: value })
          this.quill.setContents(delta, 'silent')
          this.editorText = this.$refs.editor.childNodes[0].innerHTML
          this.setCaretAtEndOfEditor()
        }
      } else {
        switch (event.keyCode) {
          case 9: // tab
            break // break to avoid default
          case 13: // enter
            this.setCaretAtEndOfEditor()
            event.preventDefault()
            break
          case 37: // left arrow
            this.$emit('move-focus', 'left', event.target)
            event.preventDefault()
            break
          case 38: // up arrow
            this.$emit('move-focus', 'up', event.target, 'ql-editor')
            event.preventDefault()
            break
          case 39: // right arrow
            this.$emit('move-focus', 'right', event.target)
            event.preventDefault()
            break
          case 40: // down arrow
            this.$emit('move-focus', 'down', event.target, 'ql-editor')
            event.preventDefault()
            break
          default: {
            // clear the text area only when some
            // visible character or backspace is pressed
            if (
              (event.keyCode === 8 ||
                (event.keyCode >= 48 &&
                  event.keyCode <= 90) ||
                (event.keyCode >= 160 &&
                  event.keyCode <= 173)) &&
              event.ctrlKey === false &&
              event.metaKey === false &&
              event.altKey === false
            ) {
              this.clearContentAndSetCursor()
            }
          }
        }
      }
    },

    emitTextDelta () {
      const html = this.$refs.editor.childNodes[0].innerHTML
      this.$emit('text-delta', html, this.id, this.index)
      this.$emit('update:modelValue', html)
    },

    setCaretAtEndOfEditor () {
      if (!this.initialised) {
        this.initQuillEditor()
      }
      if (this.quill) {
        this.quill.setSelection(this.quill.getLength(), 0)
      }
    },

    clearContentAndSetCursor () {
      this.quill.deleteText(0, this.quill.getLength())
      this.quill.setSelection(0, 0)
    },

    onfocus () {
      if (!this.initialised) {
        this.initQuillEditor()
        if (this.text) {
          const delta = this.quill.clipboard.convert({ html: this.text })
          this.quill.setContents(delta, 'silent')
          this.editorText = this.$refs.editor.childNodes[0].innerHTML
        }
        if (this.disabled) {
          this.quill.disable()
        }
      }
    },

    updateValue (text) {
      if (text && this.quill) {
        const delta = this.quill.clipboard.convert({ html: text })
        this.quill.setContents(delta, 'silent')
        this.editorText = this.$refs.editor.childNodes[0].innerHTML
      }
    }
  },

  mounted () {
    this.$refs.editor.innerHTML = this.text
  },

  beforeUnmount () {
    this.$refs.editor?.firstChild?.removeEventListener('blur', this.onBlur)
  },

  watch: {
    text (newText) {
      if (!this.initialised) {
        this.initQuillEditor()
      }
      const delta = this.quill.clipboard.convert({ html: this.text })
      this.quill.setContents(delta, 'silent')
      this.editorText = this.$refs.editor.childNodes[0].innerHTML
    },
    disabled (newVal) {
      if (newVal) {
        this.quill?.disable()
      } else {
        this.quill?.enable()
      }
    }
  }
}
</script>

<style scoped>
.ql-container.ql-snow {
  color: #1D1F22;
  font-size: 14px;
  min-height: 22px;
  font-weight: 400;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
}
</style>

<style>
.textEditor p {
  margin-bottom: 0 !important;
  color: black;
}

.textEditor .ql-editor {
  user-select: text;
  padding: 0 !important;
}

.ql-editor {
  user-select: text;
  padding: 0;
}
</style>
